import React, { useState } from "react";
import { Share2, Copy, Check, MessageCircle, Facebook } from "lucide-react";

/**
 * Share button — uses Web Share API on mobile browsers that support it,
 * otherwise shows a small dropdown with WhatsApp, Facebook, and Copy link.
 *
 * Props:
 *   title (string): sharing title
 *   text (string): pre-formatted body (used by WhatsApp)
 *   url (string, optional): defaults to window.location.href
 *   testId (string, optional): forwarded to root button
 *   compact (bool): render just the icon
 */
export default function ShareButton({ title, text, url, testId = "share-btn", compact = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const targetUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const hasWebShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const doWebShare = async () => {
    try {
      await navigator.share({ title, text, url: targetUrl });
    } catch (_e) {
      // user cancelled or unsupported; fall back
      setOpen(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Older browsers: create a temporary input
      const el = document.createElement("input");
      el.value = targetUrl;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
      document.body.removeChild(el);
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n${targetUrl}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(targetUrl)}`;

  const rootClass = compact
    ? "inline-flex items-center justify-center w-9 h-9 border border-neutral-800 text-neutral-300 hover:text-white hover:border-neutral-700 transition-colors"
    : "inline-flex items-center gap-2 px-3 h-9 border border-neutral-800 text-neutral-300 font-display uppercase tracking-widest text-[11px] hover:text-white hover:border-neutral-700 transition-colors";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid={testId}
        onClick={hasWebShare ? doWebShare : () => setOpen((v) => !v)}
        aria-label="Partager"
        className={rootClass}
      >
        <Share2 size={12} />
        {!compact && "Partager"}
      </button>

      {open && !hasWebShare && (
        <div
          className="absolute right-0 z-20 mt-2 min-w-[220px] bg-black border border-neutral-800 py-1.5 shadow-2xl"
          data-testid={`${testId}-menu`}
        >
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-whatsapp`}
            className="flex items-center gap-2 px-4 py-2 text-[12px] font-display uppercase tracking-widest text-neutral-300 hover:bg-neutral-950 hover:text-burkina-green transition-colors"
            onClick={() => setOpen(false)}
          >
            <MessageCircle size={13} /> WhatsApp
          </a>
          <a
            href={fbHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-facebook`}
            className="flex items-center gap-2 px-4 py-2 text-[12px] font-display uppercase tracking-widest text-neutral-300 hover:bg-neutral-950 hover:text-white transition-colors"
            onClick={() => setOpen(false)}
          >
            <Facebook size={13} /> Facebook
          </a>
          <button
            type="button"
            onClick={copyLink}
            data-testid={`${testId}-copy`}
            className="w-full text-left flex items-center gap-2 px-4 py-2 text-[12px] font-display uppercase tracking-widest text-neutral-300 hover:bg-neutral-950 hover:text-gold transition-colors"
          >
            {copied ? <Check size={13} className="text-burkina-green" /> : <Copy size={13} />}
            {copied ? "Copié !" : "Copier le lien"}
          </button>
        </div>
      )}
    </div>
  );
}
